import { unwrapPrivateAPI } from '@mui/x-data-grid/internals';
import {
  type GridApiPremium,
  GridColDef,
  type GridPrivateApiPremium,
  GridRowId,
  gridExpandedSortedRowEntriesSelector,
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
} from '@mui/x-data-grid-premium';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import type { GridApiRef } from '../DataGrid';

function findNonRowSpannedCell(
  apiRef: GridApiRef,
  rowId: GridRowId,
  field: GridColDef['field'],
  rowSpanScanDirection: 'up' | 'down'
) {
  const api = apiRef.current;
  if (!api) return rowId;

  const privateApi = unwrapPrivateAPI<GridPrivateApiPremium, GridApiPremium>(api);
  // Private MUI shape: degrade to "no row spanning" rather than throw in a keyboard handler.
  const rowSpanHiddenCells = privateApi?.virtualizer?.store?.state?.rowSpanning?.caches?.hiddenCells;
  if (!rowSpanHiddenCells) return rowId;
  const columnIndex = api.getColumnIndex(field);
  if (!rowSpanHiddenCells[rowId]?.[columnIndex]) {
    return rowId;
  }
  const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
  // find closest non row spanned cell in the given `rowSpanScanDirection`
  let nextRowIndex = filteredSortedRowIds.indexOf(rowId) + (rowSpanScanDirection === 'down' ? 1 : -1);
  while (nextRowIndex >= 0 && nextRowIndex < filteredSortedRowIds.length) {
    const nextRowId = filteredSortedRowIds[nextRowIndex];
    if (!rowSpanHiddenCells[nextRowId]?.[columnIndex]) {
      return nextRowId;
    }
    nextRowIndex += rowSpanScanDirection === 'down' ? 1 : -1;
  }
  return rowId;
}

/**
 * @param {number} colIndex Index of the column to focus
 * @param {GridRowId} rowId index of the row to focus
 * @param {string} closestColumnToUse Which closest column cell to use when the cell is spanned by `colSpan`.
 * @param {string} rowSpanScanDirection Which direction to search to find the next cell not hidden by `rowSpan`.
 */
export default function useGoToCell(apiRef: GridApiRef) {
  return useCallback(
    (
      colIndex: number,
      rowId: GridRowId,
      closestColumnToUse: 'left' | 'right' = 'left',
      rowSpanScanDirection: 'up' | 'down' = 'up'
    ) => {
      const api = apiRef.current;
      if (!api) return;

      const visibleSortedRows = gridExpandedSortedRowEntriesSelector(apiRef);
      const nextCellColSpanInfo = api.unstable_getCellColSpanInfo(rowId, colIndex);
      if (nextCellColSpanInfo && nextCellColSpanInfo.spannedByColSpan) {
        if (closestColumnToUse === 'left') {
          colIndex = nextCellColSpanInfo.leftVisibleCellIndex;
        } else if (closestColumnToUse === 'right') {
          colIndex = nextCellColSpanInfo.rightVisibleCellIndex;
        }
      }
      const field = gridVisibleColumnFieldsSelector(apiRef)[colIndex];
      const nonRowSpannedRowId = findNonRowSpannedCell(apiRef, rowId, field, rowSpanScanDirection);
      // `scrollToIndexes` requires a rowIndex relative to all visible rows.
      // Those rows do not include pinned rows, but pinned rows do not need scroll anyway.
      const rowIndexRelativeToAllRows = visibleSortedRows.findIndex((row) => row.id === nonRowSpannedRowId);

      if (colIndex === -1 || isNil(colIndex) || rowIndexRelativeToAllRows === -1 || isNil(rowIndexRelativeToAllRows)) {
        return;
      }

      api.scrollToIndexes({ colIndex, rowIndex: rowIndexRelativeToAllRows });

      const gotoRowId = nonRowSpannedRowId;
      const rowMode = api.getRowMode(gotoRowId);

      if (rowMode === 'view') {
        api.startRowEditMode({ id: gotoRowId, fieldToFocus: field });
      }

      api.setCellFocus(nonRowSpannedRowId, field);

      setTimeout(() => {
        const cellElement = apiRef.current?.getRowElement(gotoRowId);

        if (cellElement) {
          cellElement.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }
      });
    },
    [apiRef]
  );
}
